import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import srOnly from '@helpers/srOnly';
import { h3, paragraphRegular } from '@helpers/typography';
import styled, { css } from 'styled-components';
const Container = styled.section `
  display: block;
`;
const Form = styled.form `
  display: block;
  max-width: 530px;
`;
const Fieldset = styled.fieldset `
  display: block;

  & + & {
    margin: 24px 0 0;
  }
`;
const Legend = styled.legend `
  ${h3};

  display: block;
  margin: 0 0 12px;
  text-transform: uppercase;
`;
const Field = styled.div `
  ${paragraphRegular};

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 4px;
  margin: 0 0 22px;
  font-size: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const Label = styled.label ``;
const ControlLabel = styled.label `
  font-weight: 400;

  > input {
    ${srOnly};
  }
`;
const CheckboxLabel = styled.span `
  position: relative;
  padding-left: 32px;
  margin-top: 4px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    display: block;
    height: 18px;
    width: 18px;
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '✔';
    position: absolute;
    left: 4px;
    top: 0;
    font-size: 14px;
    height: 20px;
    line-height: 20px;
    opacity: 0;
    text-align: center;
    width: 10px;
    color: ${brand.white};
  }

  &::before {
    ${transition('border-color, background-color', 0.25)};
    border: 1px solid ${brand.black};
    background-color: ${brand.white};
    border-radius: 4px;
  }

  input:checked + & {
    &::before {
      border-color: ${brand.white};
      background-color: ${brand.black};
    }

    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: var(--invalidColor);
  }
`;
const RadioLabel = styled.span `
  position: relative;
  padding-left: 32px;

  &::after,
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 0;
    display: block;
    border-radius: 50%;
    height: 18px;
    width: 18px;
  }

  &::after {
    ${transition('opacity', 0.25)};

    content: '';
    height: 6px;
    top: 4px;
    line-height: 18px;
    opacity: 0;
    left: 7px;
    width: 6px;
    background-color: ${brand.white};
  }

  &::before {
    border: 1px solid ${brand.black};
    background-color: ${brand.white};
  }

  input:checked + & {
    &::before {
      background-color: ${brand.black};
      border-color: ${brand.white};
    }
    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: ${brand.secondary.red};
  }
`;
const RequiredLabel = styled.span ``;
const sharedInputStyles = css `
  font-weight: 400;
  ${transition('border-color, background-color')}
  ${fonts.avenir.regular};
  font-size: 16px;
  letter-spacing: 0.416px;

  display: block;
  background: transparent;
  border: 2px solid ${brand.white};
  background-color: ${brand.white};
  color: ${brand.black};
  height: 40px;
  padding: 8px 12px;
  width: 100%;
  border-radius: 4px;

  .invalid & {
    border-color: ${brand.secondary.red};
  }

  &:focus-visible {
    border-color: ${brand.black};
    outline: auto;
    outline-offset: 1px;
  }
`;
const Input = styled.input `
  ${sharedInputStyles};
`;
const SelectWrapper = styled.div `
  width: 100%;
  position: relative;
`;
const Arrow = styled.div `
  ${transition('border-color, color')};

  display: grid;
  place-items: center;
  height: 40px;
  width: 40px;
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;

  > svg {
    ${transition('transform')};

    display: block;
    height: 6px;
    width: 12px;
    color: ${brand.black};
    transform: rotate(90deg);
  }

  .invalid & {
    border-color: ${brand.secondary.red};
    color: ${brand.secondary.red};
  }
`;
const Select = styled.select `
  ${sharedInputStyles};

  appearance: none;
  padding-right: 52px;
  padding-top: 0;
  padding-bottom: 0;

  option {
    background-color: ${brand.white};
    color: ${brand.black};
  }

  &:focus ~ ${Arrow} > svg {
    transform: rotate(-90deg);
  }
`;
const Textarea = styled.textarea `
  ${sharedInputStyles};

  height: auto;
  min-height: 74px;
`;
const Error = styled.span `
  font-weight: 400;

  display: block;
  font-size: 12px;
  line-height: 22px;
  margin: -4px 0 0;
  color: var(--invalidColor);
`;
const Submit = styled.div `
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 24px 0 0;
`;
const ConfirmationMessage = styled.p `
  ${paragraphRegular};
`;
const ErrorList = styled.ul `
  display: block;
  margin: 0 0 ${fluid(32, 48)};
  color: var(--invalidColor);
`;
const ErrorListItem = styled.li `
  ${paragraphRegular};

  margin: 0 0 ${fluid(8, 12)};
`;
const FieldTitle = styled.h2 `
  display: block;
  width: 100%;
  font-size: 120%;
`;
const FieldDescription = styled.p `
  font-weight: 400;

  display: block;
  width: 100%;
  margin: 0 0 12px;
`;
const List = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px 6px;
`;
const RecaptchaWrapper = styled.div `
  width: 100%;
`;
export default {
    Arrow,
    CheckboxLabel,
    ConfirmationMessage,
    Container,
    ControlLabel,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    FieldDescription,
    Fieldset,
    FieldTitle,
    Form,
    Input,
    Label,
    Legend,
    List,
    RadioLabel,
    RecaptchaWrapper,
    RequiredLabel,
    Select,
    SelectWrapper,
    Submit,
    Textarea,
};
